import React from "react";
import { Link } from "react-router-dom";
import {
  Printer, Phone, MapPin, Clock, Mail, AlertTriangle, Stethoscope,
  Brain, Zap, Pill, ClipboardList, ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";
import { LOGO_URL } from "@/lib/assets";

const SITE = "https://manasneuropsychiatryhospital.in";

const referWhen = [
  "Depression or anxiety not responding to two adequate medication trials",
  "Any suicidal ideation, plan or recent attempt — same-day slot available",
  "First-episode psychosis, mania or catatonia",
  "Psychiatric symptoms in a patient with epilepsy, stroke, Parkinson's or head injury",
  "Behaviour or memory change in a patient over 60",
  "Delirium in a medically ill, post-operative or ICU patient",
  "Alcohol or opioid dependence needing supervised detoxification",
  "Unexplained somatic symptoms with repeatedly normal investigations",
  "Post-partum depression or psychosis",
  "Sexual dysfunction with no urological or endocrine cause found",
];

const redFlags = [
  "First psychiatric episode after 45 with no prior history",
  "Onset over days in a previously well patient",
  "Fever, headache, neck stiffness or seizures with the behaviour change",
  "Visual (rather than auditory) hallucinations",
  "Fluctuating confusion, worse at night",
  "New focal neurological signs or rapid cognitive decline",
];

const services = [
  { icon: Stethoscope, title: "General psychiatry OPD", desc: "Mon–Sat, 9 AM – 8 PM. Depression, bipolar disorder, anxiety, OCD, psychosis, insomnia, child & adolescent, geriatric and women's mental health." },
  { icon: Brain, title: "Neuropsychiatry", desc: "Epilepsy with psychiatric comorbidity, post-stroke and post-TBI syndromes, Parkinson's, dementia and memory clinic, delirium, autoimmune and metabolic causes, functional neurological disorder." },
  { icon: Zap, title: "Neuromodulation suite", desc: "rTMS (TMS physician trained at AIIMS New Delhi), tDCS, modified brief pulse therapy under anaesthesia, and ketamine IV infusion with full monitoring." },
  { icon: Pill, title: "Deaddiction", desc: "Supervised detoxification, relapse prevention, naltrexone implants, craving-reduction neuromodulation and family work." },
];

const prescribingNotes = [
  { context: "Epilepsy", note: "Sertraline / escitalopram preferred; avoid bupropion and high-dose TCAs. Account for enzyme induction with carbamazepine and phenytoin. If mood change began after levetiracetam or topiramate, consider switching the anti-seizure drug before adding an antidepressant." },
  { context: "Post-stroke", note: "SSRIs are first line; start low, go slow. Note bleeding risk with concurrent anticoagulants/antiplatelets. Emotional incontinence often responds to a low SSRI dose within 1–2 weeks. Screen for apathy separately from depression." },
  { context: "Cardiac disease", note: "Sertraline has the most reassuring post-MI data. Check QTc before and after citalopram, escitalopram and higher-dose antipsychotics." },
  { context: "Hepatic / renal impairment", note: "Prefer agents with simpler metabolism and reduce the dose; avoid long-acting benzodiazepines in hepatic impairment. Lithium requires caution and monitoring in renal impairment." },
  { context: "Pregnancy & lactation", note: "Avoid valproate in women of child-bearing age. Discuss before stopping any psychotropic abruptly — relapse risk is often the greater hazard. rTMS is a useful drug-sparing option." },
  { context: "Elderly", note: "Start at half the adult dose, review anticholinergic load, and check B12, thyroid, sodium and vitamin D before attributing symptoms to dementia." },
];

const sendWithPatient = [
  "Referral note with your provisional impression and the clinical question",
  "Current prescription with doses and duration of each trial",
  "Past psychotropic trials, doses reached and the reason each was stopped",
  "Relevant investigations: MRI/CT films, EEG, thyroid, B12, LFT/RFT, electrolytes",
  "Discharge summaries from any previous admission",
  "Name and number of the family member who knows the history best",
];

export default function ForPhysicians() {
  useSEO({
    title: "Referral Information for Physicians — Manas Neuropsychiatry Hospital",
    description:
      "Referral summary for physicians and general practitioners in Bhavnagar and Saurashtra: when to refer to Manas Neuropsychiatry Hospital, red flags needing a neuropsychiatric work-up, services available (OPD, neuromodulation, deaddiction), prescribing notes in epilepsy/stroke/cardiac/pregnancy, and what to send with the patient. Printable one-page summary.",
    image: LOGO_URL,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "@id": `${SITE}/for-physicians`,
      url: `${SITE}/for-physicians`,
      name: "Referral Information for Physicians",
      inLanguage: "en-IN",
      audience: { "@type": "Audience", audienceType: "Physicians" },
      about: { "@type": "MedicalSpecialty", name: "Neuropsychiatry" },
      publisher: { "@id": `${SITE}/#organization` },
    },
  });

  return (
    <div data-testid="for-physicians-page" className="print-sheet">
      {/* Screen-only intro */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-10 no-print">
        <p className="eyebrow text-[#A39171] mb-6">For referring physicians</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          One page to keep <span className="italic text-[#E07A5F]">in your consulting room</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          When to refer, what needs a brain work-up before a psychiatric label, what we can offer your
          patient in Bhavnagar, and the prescribing cautions that come up most often. Print it, or save it
          as a PDF.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button
            data-testid="print-referral-btn"
            onClick={() => window.print()}
            className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium"
          >
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
          <a href="tel:+919427560101" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            Discuss a case: +91 94275 60101
          </a>
        </div>
      </section>

      {/* PRINTABLE SHEET */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 pb-24 print:px-0 print:pb-0 print:max-w-none">
        <article className="bg-[#FDFBF7] border border-[#A39171]/25 rounded-3xl p-8 md:p-12 print:border-0 print:rounded-none print:p-0" data-testid="referral-sheet">
          {/* Letterhead */}
          <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 pb-6 border-b-2 border-[#2D2623]">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Manas Neuropsychiatry Hospital" className="w-14 h-14 object-contain" />
              <div>
                <p className="font-display text-2xl md:text-3xl text-[#2D2623] leading-none">Manas Neuropsychiatry Hospital</p>
                <p className="text-sm text-[#5C524E] mt-1.5">Neuropsychiatry · Deaddiction · Sexology · Neuromodulation · Since 1989</p>
              </div>
            </div>
            <div className="text-sm text-[#5C524E] sm:text-right leading-relaxed">
              <p className="font-semibold text-[#2D2623]">+91 94275 60101</p>
              <p>Mon–Sat · 9 AM – 8 PM</p>
              <p>manasneuropsychiatryhospital.in</p>
            </div>
          </header>

          <p className="mt-6 text-[#5C524E] leading-relaxed">
            <strong className="text-[#2D2623]">Dr. Kamlesh A. Shah</strong> — MD (Neuropsychiatry), MBBS · Consultant Neuropsychiatrist ·
            <strong className="text-[#2D2623]"> Dr. Dharmin K. Shah</strong> — MD (Psychiatry), TMS Physician (AIIMS New Delhi) ·
            2nd floor, Carlton Square, near Madhavdeep Complex, Kalanala, Bhavnagar 364001.
            Referrals accepted directly — no formality required. Written reply sent back to the referring doctor on request.
          </p>

          {/* When to refer + red flags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <section data-testid="refer-when">
              <h2 className="font-display text-2xl text-[#2D2623] mb-3 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#E07A5F]" /> When to refer
              </h2>
              <ul className="space-y-1.5">
                {referWhen.map((r) => (
                  <li key={r} className="text-sm text-[#5C524E] leading-snug flex gap-2">
                    <span className="text-[#E07A5F]">›</span><span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section data-testid="refer-red-flags">
              <h2 className="font-display text-2xl text-[#2D2623] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#E07A5F]" /> Needs a brain work-up first
              </h2>
              <ul className="space-y-1.5">
                {redFlags.map((r) => (
                  <li key={r} className="text-sm text-[#5C524E] leading-snug flex gap-2">
                    <span className="text-[#E07A5F]">›</span><span>{r}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#5C524E] mt-3 leading-snug">
                Consider MRI/CT, EEG, thyroid, B12, electrolytes, LFT/RFT, copper studies or autoimmune
                antibodies as the picture dictates. Autoimmune encephalitis, Wilson's disease, tumours and
                thyroid disease are all treated as primary psychiatric illness at times.
              </p>
            </section>
          </div>

          {/* Services */}
          <section className="mt-8" data-testid="refer-services">
            <h2 className="font-display text-2xl text-[#2D2623] mb-3">What we can offer your patient</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {services.map((s) => (
                <div key={s.title} className="flex gap-3">
                  <s.icon className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                  <p className="text-sm text-[#5C524E] leading-snug">
                    <strong className="text-[#2D2623]">{s.title}:</strong> {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Prescribing notes */}
          <section className="mt-8" data-testid="refer-prescribing">
            <h2 className="font-display text-2xl text-[#2D2623] mb-3">Prescribing notes that come up most often</h2>
            <table className="w-full text-sm border-collapse">
              <tbody>
                {prescribingNotes.map((p) => (
                  <tr key={p.context} className="border-b border-[#A39171]/20 align-top">
                    <th className="text-left font-semibold text-[#2D2623] py-2 pr-4 w-40 whitespace-nowrap">{p.context}</th>
                    <td className="py-2 text-[#5C524E] leading-snug">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Send with patient + contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <section data-testid="refer-send">
              <h2 className="font-display text-2xl text-[#2D2623] mb-3">Please send with the patient</h2>
              <ul className="space-y-1.5">
                {sendWithPatient.map((r) => (
                  <li key={r} className="text-sm text-[#5C524E] leading-snug flex gap-2">
                    <span className="text-[#E07A5F]">›</span><span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section data-testid="refer-contact" className="bg-[#F4EFE6] rounded-2xl p-6 print:bg-transparent print:border print:border-[#2D2623] print:rounded-none">
              <h2 className="font-display text-2xl text-[#2D2623] mb-3">Contact & urgent referrals</h2>
              <ul className="space-y-2 text-sm text-[#5C524E]">
                <li className="flex gap-2"><Phone className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" /><span><strong className="text-[#2D2623]">+91 94275 60101</strong> — reception & same-day urgent slots</span></li>
                <li className="flex gap-2"><Clock className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" /><span>OPD Monday to Saturday, 9 AM – 8 PM</span></li>
                <li className="flex gap-2"><MapPin className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" /><span>2nd floor, Carlton Square, near Madhavdeep Complex, Kalanala, Bhavnagar 364001</span></li>
                <li className="flex gap-2"><Mail className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" /><span>manasneuropsychiatryhospital@gmail.com</span></li>
              </ul>
              <p className="text-xs text-[#5C524E] mt-4 leading-snug">
                For an actively suicidal patient, catatonia, food refusal or acute behavioural emergency,
                telephone before sending so the slot and, if needed, admission are arranged.
              </p>
            </section>
          </div>

          <footer className="mt-8 pt-4 border-t border-[#A39171]/30 text-xs text-[#5C524E] flex flex-col sm:flex-row justify-between gap-2">
            <span>Manas Neuropsychiatry Hospital, Bhavnagar · Established 1989 · manasneuropsychiatryhospital.in/for-physicians</span>
            <span>Patient confidentiality maintained; a written reply is sent to the referring doctor on request.</span>
          </footer>
        </article>

        <div className="mt-10 flex flex-wrap gap-3 no-print">
          <Link to="/neuropsychiatry" data-testid="refer-link-np" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
            Neuropsychiatry service <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
          </Link>
          <Link to="/general-psychiatry" data-testid="refer-link-gp" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
            General psychiatry <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
          </Link>
          <Link to="/neuromodulation" data-testid="refer-link-nm" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
            Neuromodulation suite <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
          </Link>
          <Link to="/case-reports" data-testid="refer-link-cases" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
            Published case reports <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}
